/** /map — the world, searching. Public missing cases and active disasters as
 *  privacy-safe approximate pins (coordinates fuzzed to an ~11 km grid;
 *  minors to ~55 km). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { Globe2, Loader2 } from "lucide-react";
import StatusBadge from "@/components/site/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const COLORS = {
  ACTIVE: "#a34a2a",
  FOUND_SAFE: "#3d5a3c",
  LOCATED: "#3d5a3c",
  REUNITED: "#3d5a3c",
  disaster: "#2b1c0f",
};

function dot(color, size = 14) {
  return L.divIcon({
    className: "",
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #f4ead5;box-shadow:0 1px 6px rgba(43,28,15,.4)"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Fit the viewport to the pins once they arrive (with a sane max zoom). */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 5 });
  }, [points, map]);
  return null;
}

export default function MapPage() {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/v1/missing/map-points`)
      .then(({ data }) => setPoints(data.points || []))
      .catch(() => setPoints([]));
  }, []);

  const cases = (points || []).filter((p) => p.kind === "case");
  const disasters = (points || []).filter((p) => p.kind === "disaster");

  return (
    <main data-testid="map-page" className="pt-32 md:pt-36">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <div className="overline text-[color:var(--ink-soft)] mb-6 flex items-center gap-2">
          <Globe2 size={13} className="text-[color:var(--rust)]" /> § The world, searching
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(40px, 6vw, 100px)" }}
        >
          Every pin is <span className="font-italic-serif italic text-[color:var(--rust)]">someone's person.</span>
        </motion.h1>
        <p className="mt-5 max-w-2xl text-[16px] leading-[1.65] text-[color:var(--ink-soft)]">
          Missing people and active disasters, plotted approximately — never an exact address.
          Pins are blurred to a safe distance on purpose.
        </p>
        <div className="mt-4 flex flex-wrap gap-5 numeric-index text-[color:var(--ink-soft)]" data-testid="map-legend">
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.ACTIVE }} /> Missing ({cases.filter((c) => c.status === "ACTIVE").length})
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.FOUND_SAFE }} /> Found / reunited ({cases.filter((c) => c.status !== "ACTIVE").length})
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.disaster }} /> Disasters ({disasters.length})
          </span>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        <div className="border border-[color:var(--sepia)]/30 shadow-[0_24px_60px_-30px_rgba(43,28,15,0.5)]" data-testid="map-container">
          {points === null ? (
            <div className="h-[70vh] flex items-center justify-center text-[color:var(--ink-soft)]">
              <Loader2 size={18} className="animate-spin mr-2" /> Unfolding the map…
            </div>
          ) : (
            <MapContainer
              center={[22, 10]}
              zoom={2}
              minZoom={2}
              style={{ height: "70vh", width: "100%", background: "var(--parchment-2)" }}
              worldCopyJump
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                className="heritage-tiles"
              />
              <FitBounds points={points} />
              {cases.map((p) => (
                <Marker key={`c-${p.id}`} position={[p.lat, p.lon]} icon={dot(COLORS[p.status] || COLORS.ACTIVE)}>
                  <Popup>
                    <div style={{ fontFamily: "Georgia, serif", minWidth: 180 }}>
                      <div style={{ marginBottom: 6 }}>
                        <StatusBadge status={p.status} />
                      </div>
                      <strong style={{ fontSize: 15 }}>{p.name}</strong>
                      <div style={{ fontSize: 12, color: "#7a6a54", margin: "4px 0" }}>{p.place} · approx.</div>
                      <a href={`/missing/case/${p.id}`} data-testid={`map-case-${p.id}`} style={{ color: "#a34a2a", fontSize: 13 }}>
                        Open the case →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {disasters.map((p) => (
                <Marker key={`d-${p.id}`} position={[p.lat, p.lon]} icon={dot(COLORS.disaster, 18)}>
                  <Popup>
                    <div style={{ fontFamily: "Georgia, serif", minWidth: 180 }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#7a6a54", marginBottom: 4 }}>
                        Disaster · {p.status}
                      </div>
                      <strong style={{ fontSize: 15 }}>{p.name}</strong>
                      <div style={{ fontSize: 12, color: "#7a6a54", margin: "4px 0" }}>{p.place} · approx.</div>
                      <a href={`/disasters/${p.id}`} data-testid={`map-disaster-${p.id}`} style={{ color: "#a34a2a", fontSize: 13 }}>
                        Open the record →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
        <p className="mt-3 numeric-index text-[color:var(--ink-soft)]">
          Map © OpenStreetMap contributors · pins are approximate by design — exact locations are never published.
        </p>
      </section>
    </main>
  );
}
